import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Trash2, Save, Edit3, Loader2, ArrowLeft, Mail } from "lucide-react";

const empty_course = () => ({
  slug: "",
  title: "",
  subtitle: "",
  description: "",
  cover_image: "",
  price_chf: 0,
  track: "academy",
  language: "de",
  is_published: true,
  modules: [],
});

export const CourseEditor = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // course slug | "new" | null
  const [form, setForm] = useState(empty_course());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const reload = async () => {
    const r = await api.get("/lms/admin/courses");
    setCourses(r.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/admin/login"); return; }
    reload();
  }, [user, loading, nav]);

  const startNew = () => { setEditing("new"); setForm(empty_course()); };

  const startEdit = async (slug) => {
    const r = await api.get(`/lms/admin/courses/${slug}`);
    setForm({ ...empty_course(), ...r.data });
    setEditing(slug);
  };

  const cancel = () => { setEditing(null); setForm(empty_course()); setMsg(""); };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      if (editing === "new") {
        await api.post("/lms/admin/courses", form);
      } else {
        await api.put(`/lms/admin/courses/${editing}`, form);
      }
      setMsg("Gespeichert ✓");
      await reload();
      setTimeout(cancel, 800);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Kurs "${slug}" löschen?`)) return;
    await api.delete(`/lms/admin/courses/${slug}`);
    await reload();
  };

  // Module / Lesson / Quiz manipulators
  const updateModule = (mi, patch) => {
    const ms = [...form.modules];
    ms[mi] = { ...ms[mi], ...patch };
    setForm({ ...form, modules: ms });
  };
  const addModule = () => setForm({ ...form, modules: [...form.modules, { title: "Neues Modul", summary: "", lessons: [], quiz: [], pass_score: 70 }] });
  const removeModule = (mi) => setForm({ ...form, modules: form.modules.filter((_, i) => i !== mi) });

  const addLesson = (mi) => {
    const ms = [...form.modules];
    ms[mi].lessons = [...(ms[mi].lessons || []), { title: "Neue Lektion", body: "", video_url: "", duration_min: 10 }];
    setForm({ ...form, modules: ms });
  };
  const updateLesson = (mi, li, patch) => {
    const ms = [...form.modules];
    ms[mi].lessons[li] = { ...ms[mi].lessons[li], ...patch };
    setForm({ ...form, modules: ms });
  };
  const removeLesson = (mi, li) => {
    const ms = [...form.modules];
    ms[mi].lessons = ms[mi].lessons.filter((_, i) => i !== li);
    setForm({ ...form, modules: ms });
  };

  const addQuiz = (mi) => {
    const ms = [...form.modules];
    ms[mi].quiz = [...(ms[mi].quiz || []), { q: "Neue Frage?", options: ["Option A", "Option B"], correct_index: 0 }];
    setForm({ ...form, modules: ms });
  };
  const updateQuiz = (mi, qi, patch) => {
    const ms = [...form.modules];
    ms[mi].quiz[qi] = { ...ms[mi].quiz[qi], ...patch };
    setForm({ ...form, modules: ms });
  };
  const removeQuiz = (mi, qi) => {
    const ms = [...form.modules];
    ms[mi].quiz = ms[mi].quiz.filter((_, i) => i !== qi);
    setForm({ ...form, modules: ms });
  };

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid="course-editor-page">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="btn-ghost text-sm"><ArrowLeft size={14} /> Admin</Link>
            <h1 className="headline-xl text-4xl mt-3">Course Editor</h1>
          </div>
          {!editing && <button onClick={startNew} className="btn-primary text-sm" data-testid="ce-new"><Plus size={14} /> Neuer Kurs</button>}
        </div>

        {!editing && (
          <div className="space-y-3" data-testid="ce-courses-list">
            {courses.map((c) => (
              <div key={c.id} className="glass rounded-xl p-5 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="serif text-xl">{c.title}</div>
                  <div className="text-xs text-white/45 mt-1">{c.slug} · {c.track} · {c.modules?.length || 0} Module · CHF {c.price_chf}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => startEdit(c.slug)} className="btn-secondary text-sm" data-testid={`ce-edit-${c.slug}`}><Edit3 size={12} /> Bearbeiten</button>
                  <button onClick={() => del(c.slug)} className="btn-ghost text-sm text-red-300"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="glass rounded-2xl p-6 md:p-8 space-y-5" data-testid="ce-editor-form">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/55 uppercase tracking-widest">Slug</label>
                <input className="sat-input mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="my-course" />
              </div>
              <div>
                <label className="text-xs text-white/55 uppercase tracking-widest">Track</label>
                <select className="sat-input mt-1" value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })}>
                  <option value="academy">Academy</option><option value="mentor">Mentor</option><option value="corporate">Corporate</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-white/55 uppercase tracking-widest">Titel</label>
                <input className="sat-input mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-white/55 uppercase tracking-widest">Untertitel</label>
                <input className="sat-input mt-1" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-white/55 uppercase tracking-widest">Beschreibung</label>
                <textarea className="sat-input mt-1" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-white/55 uppercase tracking-widest">Preis CHF</label>
                <input type="number" className="sat-input mt-1" value={form.price_chf} onChange={(e) => setForm({ ...form, price_chf: +e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-white/55 uppercase tracking-widest">Cover Image URL</label>
                <input className="sat-input mt-1" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
              </div>
            </div>

            <div className="border-t border-white/8 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif text-2xl">Module</h3>
                <button onClick={addModule} className="btn-secondary text-sm"><Plus size={12} /> Modul</button>
              </div>
              <div className="space-y-4">
                {form.modules.map((m, mi) => (
                  <details key={mi} className="border border-white/8 rounded-xl p-4">
                    <summary className="cursor-pointer flex justify-between items-center">
                      <span className="serif text-lg">{mi + 1}. {m.title}</span>
                      <button onClick={(e) => { e.preventDefault(); removeModule(mi); }} className="text-red-300 text-xs">Löschen</button>
                    </summary>
                    <div className="mt-4 space-y-3">
                      <input className="sat-input" value={m.title} onChange={(e) => updateModule(mi, { title: e.target.value })} placeholder="Modul-Titel" />
                      <input className="sat-input" value={m.summary} onChange={(e) => updateModule(mi, { summary: e.target.value })} placeholder="Zusammenfassung" />
                      <div className="flex items-center gap-3 text-sm">
                        <label className="text-white/55">Pass-Score:</label>
                        <input type="number" className="sat-input w-24" value={m.pass_score} onChange={(e) => updateModule(mi, { pass_score: +e.target.value })} />
                      </div>

                      <div className="border-t border-white/5 pt-3">
                        <div className="flex justify-between items-center mb-2"><span className="overline">Lektionen ({m.lessons?.length || 0})</span>
                          <button onClick={() => addLesson(mi)} className="btn-ghost text-xs">+ Lektion</button></div>
                        <div className="space-y-2">
                          {(m.lessons || []).map((l, li) => (
                            <div key={li} className="border border-white/5 rounded-lg p-3 space-y-2">
                              <div className="flex justify-between"><span className="text-xs text-white/45">L{li + 1}</span>
                                <button onClick={() => removeLesson(mi, li)} className="text-red-300 text-xs">x</button></div>
                              <input className="sat-input" value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} placeholder="Titel" />
                              <input className="sat-input" value={l.video_url || ""} onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })} placeholder="Video Embed URL" />
                              <textarea className="sat-input" rows={3} value={l.body} onChange={(e) => updateLesson(mi, li, { body: e.target.value })} placeholder="Lektions-Text" />
                              <input type="number" className="sat-input w-32" value={l.duration_min} onChange={(e) => updateLesson(mi, li, { duration_min: +e.target.value })} placeholder="Min" />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-white/5 pt-3">
                        <div className="flex justify-between items-center mb-2"><span className="overline">Quiz ({m.quiz?.length || 0})</span>
                          <button onClick={() => addQuiz(mi)} className="btn-ghost text-xs">+ Frage</button></div>
                        <div className="space-y-2">
                          {(m.quiz || []).map((q, qi) => (
                            <div key={qi} className="border border-white/5 rounded-lg p-3 space-y-2">
                              <div className="flex justify-between"><span className="text-xs text-white/45">F{qi + 1}</span>
                                <button onClick={() => removeQuiz(mi, qi)} className="text-red-300 text-xs">x</button></div>
                              <input className="sat-input" value={q.q} onChange={(e) => updateQuiz(mi, qi, { q: e.target.value })} placeholder="Frage" />
                              {(q.options || []).map((o, oi) => (
                                <div key={oi} className="flex gap-2">
                                  <input className="sat-input flex-1" value={o} onChange={(e) => { const opts = [...q.options]; opts[oi] = e.target.value; updateQuiz(mi, qi, { options: opts }); }} placeholder={`Option ${oi + 1}`} />
                                  <button onClick={() => updateQuiz(mi, qi, { correct_index: oi })} className={`px-3 rounded ${q.correct_index === oi ? "bg-[#D4AF37] text-[#081328]" : "border border-white/10 text-white/60"}`}>✓</button>
                                  <button onClick={() => updateQuiz(mi, qi, { options: q.options.filter((_, i) => i !== oi) })} className="text-red-300 px-2">x</button>
                                </div>
                              ))}
                              <button onClick={() => updateQuiz(mi, qi, { options: [...q.options, ""] })} className="btn-ghost text-xs">+ Option</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-white/8">
              <button onClick={save} disabled={busy} className="btn-primary" data-testid="ce-save">{busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Speichern</button>
              <button onClick={cancel} className="btn-ghost text-sm">Abbrechen</button>
              {msg && <span className="text-sm text-[#D4AF37] self-center ml-3">{msg}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Drip Editor ----------
const empty_campaign = () => ({ name: "Neue Kampagne", trigger: "newsletter_signup", is_active: true, language: "de", steps: [] });

export const DripEditor = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty_campaign());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const reload = async () => {
    const r = await api.get("/admin/drips");
    setCampaigns(r.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/admin/login"); return; }
    reload();
  }, [user, loading, nav]);

  const startEdit = (c) => { setForm({ ...c }); setEditing(c.id); };
  const startNew = () => { setForm(empty_campaign()); setEditing("new"); };
  const cancel = () => { setEditing(null); setForm(empty_campaign()); setMsg(""); };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      if (editing === "new") await api.post("/admin/drips", form);
      else await api.put(`/admin/drips/${editing}`, form);
      setMsg("Gespeichert ✓"); await reload(); setTimeout(cancel, 800);
    } catch (e) { setMsg(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { if (!window.confirm("Kampagne löschen?")) return; await api.delete(`/admin/drips/${id}`); await reload(); };

  const addStep = () => setForm({ ...form, steps: [...form.steps, { delay_hours: 24, subject: "Neue Mail", body_html: "<p>Hallo {{first_name}},</p>" }] });
  const updateStep = (i, patch) => { const s = [...form.steps]; s[i] = { ...s[i], ...patch }; setForm({ ...form, steps: s }); };
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, j) => j !== i) });

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid="drip-editor-page">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="btn-ghost text-sm"><ArrowLeft size={14} /> Admin</Link>
            <h1 className="headline-xl text-4xl mt-3">Drip-Kampagnen <Mail size={28} className="inline ml-2 text-[#D4AF37]" /></h1>
          </div>
          {!editing && <button onClick={startNew} className="btn-primary text-sm"><Plus size={14} /> Neue Kampagne</button>}
        </div>

        {!editing && (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c.id} className="glass rounded-xl p-5 flex items-center justify-between" data-testid={`drip-${c.id}`}>
                <div>
                  <div className="serif text-xl">{c.name}</div>
                  <div className="text-xs text-white/45 mt-1">Trigger: <span className="text-[#D4AF37]">{c.trigger}</span> · {c.steps?.length} Schritte · {c.language.toUpperCase()} · {c.is_active ? "aktiv" : "pausiert"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(c)} className="btn-secondary text-sm"><Edit3 size={12} /></button>
                  <button onClick={() => del(c.id)} className="btn-ghost text-sm text-red-300"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="glass rounded-2xl p-6 md:p-8 space-y-5" data-testid="drip-editor-form">
            <div className="grid md:grid-cols-3 gap-3">
              <input className="sat-input md:col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
              <select className="sat-input" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}>
                <option value="newsletter_signup">newsletter_signup</option>
                <option value="quiz_submitted">quiz_submitted</option>
                <option value="course_completed">course_completed</option>
              </select>
              <select className="sat-input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                <option value="de">Deutsch</option><option value="en">English</option>
              </select>
              <label className="flex items-center gap-2 px-3 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> aktiv
              </label>
            </div>

            <div className="border-t border-white/8 pt-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="serif text-xl">Schritte</h3>
                <button onClick={addStep} className="btn-secondary text-sm"><Plus size={12} /> Schritt</button>
              </div>
              <div className="text-xs text-white/45 mb-4">Verfügbare Platzhalter: <code className="text-[#D4AF37]">{"{{first_name}}"}</code> · <code className="text-[#D4AF37]">{"{{weakest_rule_name}}"}</code></div>
              <div className="space-y-3">
                {form.steps.map((s, i) => (
                  <div key={i} className="border border-white/8 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between"><span className="num-mark">{String(i + 1).padStart(2, "0")}</span>
                      <button onClick={() => removeStep(i)} className="text-red-300 text-xs"><Trash2 size={12} /></button></div>
                    <div className="flex gap-3 items-center">
                      <label className="text-xs text-white/55">Delay (h):</label>
                      <input type="number" className="sat-input w-32" value={s.delay_hours} onChange={(e) => updateStep(i, { delay_hours: +e.target.value })} />
                    </div>
                    <input className="sat-input" value={s.subject} onChange={(e) => updateStep(i, { subject: e.target.value })} placeholder="Betreff" />
                    <textarea className="sat-input font-mono text-xs" rows={6} value={s.body_html} onChange={(e) => updateStep(i, { body_html: e.target.value })} placeholder="HTML-Body" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-white/8">
              <button onClick={save} disabled={busy} className="btn-primary"><Save size={14} /> Speichern</button>
              <button onClick={cancel} className="btn-ghost text-sm">Abbrechen</button>
              {msg && <span className="text-sm text-[#D4AF37] self-center ml-3">{msg}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseEditor;
